/**
 * Launch menu stops (2026-10-20): the Mattertags and highlight reel stops
 * listed under each model in the public Launch menu, so a visitor can jump
 * straight to a room or configuration instead of the model's start view
 * (the Treedis room menu Honeywell relied on).
 *
 * Mattertags come from the synced `matterport_details.provider_mattertags`
 * copy, so every model in the menu lists them before it is opened. The
 * highlight reel has no Model API field; it is read live from the open
 * viewer (`Tour.getData`) and only the active model lists its reel stops.
 * Only labels and provider ids reach the public page, never descriptions,
 * media, or positions.
 */

export type LaunchTagStop = {
  kind: "tag"; id: string; label: string;
  /** Plain-text tag description (room size, seating, AV), already visible in the tour. */
  details?: string;
};
export type LaunchReelStop = {
  kind: "reel"; index: number; label: string;
  /** The highlight's own preview image from the viewer (https only). */
  thumbnailUrl?: string;
};
export type LaunchStop = LaunchTagStop | LaunchReelStop;

/** Bounds one model's list; a model with hundreds of equipment tags stays usable. */
export const MAX_LAUNCH_STOPS = 60;
const MAX_LABEL = 120;
const ID = /^[A-Za-z0-9._:-]{1,128}$/;

function cleanLabel(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const label = value.replace(/\s+/g, " ").trim();
  if (!label) return null;
  return label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;
}

const MAX_DETAILS = 300;

/** Markdown links and emphasis reduced to text; whitespace collapsed; bounded. */
function cleanDetails(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`#>]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return null;
  return text.length > MAX_DETAILS ? `${text.slice(0, MAX_DETAILS - 1)}…` : text;
}

const naturalOrder = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

/**
 * Visible, labelled Mattertags as menu stops, natural-sorted by label.
 * Accepts the stored sync payload (array of `{ id, label, enabled }`) or
 * the SDK's observable collection (keyed by sid); anything else is empty.
 */
export function mattertagStops(raw: unknown): LaunchTagStop[] {
  const entries: Array<[string | null, unknown]> = Array.isArray(raw)
    ? raw.map((tag) => [null, tag])
    : raw && typeof raw === "object"
      ? Object.entries(raw as Record<string, unknown>)
      : [];
  const seen = new Set<string>();
  const stops: LaunchTagStop[] = [];
  for (const [key, value] of entries) {
    if (!value || typeof value !== "object") continue;
    const tag = value as Record<string, unknown>;
    const id = typeof tag.id === "string" ? tag.id : typeof tag.sid === "string" ? tag.sid : key;
    if (!id || !ID.test(id) || seen.has(id) || tag.enabled === false) continue;
    const label = cleanLabel(tag.label);
    if (!label) continue;
    seen.add(id);
    const details = cleanDetails(tag.description);
    stops.push(details ? { kind: "tag", id, label, details } : { kind: "tag", id, label });
  }
  return stops
    .sort((a, b) => naturalOrder.compare(a.label, b.label))
    .slice(0, MAX_LAUNCH_STOPS);
}

/** Named highlight reel snapshots in reel order. Unnamed stops are skipped. */
export function reelStops(raw: unknown): LaunchReelStop[] {
  if (!Array.isArray(raw)) return [];
  const stops: LaunchReelStop[] = [];
  raw.forEach((snapshot, index) => {
    const row = snapshot && typeof snapshot === "object" ? snapshot as Record<string, unknown> : null;
    const label = row ? cleanLabel(row.name) : null;
    if (!label) return;
    const thumb = typeof row!.thumbnailUrl === "string" && /^https:\/\//.test(row!.thumbnailUrl) && row!.thumbnailUrl.length <= 2048
      ? row!.thumbnailUrl : null;
    stops.push(thumb ? { kind: "reel", index, label, thumbnailUrl: thumb } : { kind: "reel", index, label });
  });
  return stops.slice(0, MAX_LAUNCH_STOPS);
}

/** Stable key for React lists and pending-stop comparison. */
export function launchStopKey(stop: LaunchStop): string {
  return stop.kind === "tag" ? `tag:${stop.id}` : `reel:${stop.index}`;
}

/**
 * Which places a tour's menu links to, chosen per tour in SpaceLink menu
 * settings and stored as the Launch block's `config.menu_place_links`.
 * One source at a time, so a menu never mixes equipment tags with
 * highlights. Unset (every tour saved before the setting) means tags.
 */
export type LaunchPlaceLinks = "tags" | "highlights" | "off";
export const LAUNCH_PLACE_LINK_OPTIONS: ReadonlyArray<{ value: LaunchPlaceLinks; label: string; description: string }> = [
  { value: "tags", label: "Mattertags", description: "Each model lists its Mattertags, even before it is opened." },
  { value: "highlights", label: "Highlight reel", description: "The open model lists its named highlight reel stops." },
  { value: "off", label: "Off", description: "The menu only switches between models." },
];

export function readLaunchPlaceLinks(value: unknown): LaunchPlaceLinks {
  return value === "highlights" || value === "off" ? value : "tags";
}

export function isLaunchPlaceLinks(value: unknown): value is LaunchPlaceLinks {
  return value === "tags" || value === "highlights" || value === "off";
}

/**
 * A place in a share link: `#m=<deliverable>&place=<model>~tag:<id>` or
 * `place=<model>~reel:<index>`. `#m` still selects the model; the place
 * names its model too, so it runs only in that model's viewer and only
 * selects a stop inside it, never a model or access.
 */
export function encodeLaunchPlace(modelId: string, stop: LaunchStop): string {
  return `place=${encodeURIComponent(`${modelId}~${launchStopKey(stop)}`)}`;
}

export function parseLaunchPlace(hash: string): { modelId: string; stop: LaunchStop } | null {
  let value: string | null;
  try { value = new URLSearchParams(hash.replace(/^#/, "")).get("place"); } catch { return null; }
  const match = value ? /^([A-Za-z0-9._:-]{1,128})~(?:tag:([A-Za-z0-9._:-]{1,128})|reel:(\d{1,4}))$/.exec(value) : null;
  if (!match) return null;
  const stop: LaunchStop = match[2] ? { kind: "tag", id: match[2], label: "" } : { kind: "reel", index: Number(match[3]), label: "" };
  return { modelId: match[1]!, stop };
}

/**
 * Per-tour place curation from SpaceLink settings, stored as the Launch
 * block's `config.menu_place_overrides`: hidden places and renamed places,
 * keyed by `launchStopKey` (`tag:<id>`).
 */
export type LaunchPlaceOverrides = { hidden: string[]; labels: Record<string, string> };

const KEY = /^(?:tag:[A-Za-z0-9._:-]{1,128}|reel:\d{1,4})$/;
const MAX_OVERRIDES = 2000;

/** Malformed settings read as none, so a bad save never hides a whole menu. */
export function readLaunchPlaceOverrides(value: unknown): LaunchPlaceOverrides | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const hidden = Array.isArray(row.hidden)
    ? Array.from(new Set(row.hidden.filter((key): key is string => typeof key === "string" && KEY.test(key)))).slice(0, MAX_OVERRIDES)
    : [];
  const labels: Record<string, string> = {};
  if (row.labels && typeof row.labels === "object" && !Array.isArray(row.labels)) {
    for (const [key, label] of Object.entries(row.labels as Record<string, unknown>).slice(0, MAX_OVERRIDES)) {
      const clean = cleanLabel(label);
      if (KEY.test(key) && clean) labels[key] = clean;
    }
  }
  return hidden.length || Object.keys(labels).length ? { hidden, labels } : null;
}

/** Validates an editor save exactly, rather than silently dropping entries. */
export function isLaunchPlaceOverrides(value: unknown): value is LaunchPlaceOverrides {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  if (!Array.isArray(row.hidden) || row.hidden.length > MAX_OVERRIDES ||
    !row.hidden.every((key) => typeof key === "string" && KEY.test(key))) return false;
  if (!row.labels || typeof row.labels !== "object" || Array.isArray(row.labels)) return false;
  const labels = Object.entries(row.labels as Record<string, unknown>);
  return labels.length <= MAX_OVERRIDES && labels.every(([key, label]) =>
    KEY.test(key) && typeof label === "string" && label.trim().length > 0 && label.length <= MAX_LABEL);
}

/** Hide and rename places for one tour; unknown keys are ignored. */
export function applyLaunchPlaceOverrides<T extends LaunchStop>(stops: readonly T[], overrides: LaunchPlaceOverrides | null): T[] {
  if (!overrides) return [...stops];
  const hidden = new Set(overrides.hidden);
  return stops
    .filter((stop) => !hidden.has(launchStopKey(stop)))
    .map((stop) => {
      const label = overrides.labels[launchStopKey(stop)];
      return label ? { ...stop, label } : stop;
    });
}

const MONTH = "(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";
const DATE_SEGMENT = new RegExp(`^(?:${MONTH}\\.?\\s+\\d{1,2},?\\s+\\d{4}|\\d{4}-\\d{2}-\\d{2}|\\d{1,2}/\\d{1,2}/\\d{2,4})$`, "i");
const ADDRESS_SEGMENT = /\b\d{5}(?:-\d{4})?\s*$|,\s*[A-Z]{2}\s*$/;

/**
 * A menu name without the parts the menu already shows: the capture date
 * (returned separately) and the street address. "January 5, 2026 - 14th
 * Floor - A B C Full Room - 855 S. Mint, Charlotte, North Carolina, 28202"
 * becomes "14th Floor · A B C Full Room" dated "January 5, 2026". Labels
 * without " - " parts, or with nothing left, are kept as they are.
 */
export function shortLaunchLabel(label: string): { name: string; date: string | null } {
  const parts = label.split(/\s+[-–—]\s+/).map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) return { name: label, date: null };
  const date = parts.find((part) => DATE_SEGMENT.test(part)) ?? null;
  const kept = parts.filter((part) => part !== date && !ADDRESS_SEGMENT.test(part));
  if (kept.length === 0) return { name: label, date: null };
  return { name: kept.join(" · "), date };
}


/**
 * Same place across configurations (2026-10-20). Each Matterport model has
 * its own coordinates, so "the same spot" in another setup of a room is
 * found by name: "Townhall ABC" and "Townhall A B & C" are one place.
 */
export function launchPlaceNameKey(label: string): string {
  return label.toLocaleLowerCase().replace(/&|\band\b/g, "").replace(/[^\p{L}\p{N}]+/gu, "");
}

type Point = { x: number; y: number; z: number };
export const SAME_PLACE_RADIUS_M = 10;

/** The positioned place nearest the camera, within `SAME_PLACE_RADIUS_M`. */
export function nearestLaunchPlace(
  camera: Point | null | undefined,
  places: ReadonlyArray<{ label: string; position: Point }>,
): string | null {
  if (!camera) return null;
  let best: { label: string; distance: number } | null = null;
  for (const place of places) {
    const distance = Math.hypot(place.position.x - camera.x, place.position.y - camera.y, place.position.z - camera.z);
    if (Number.isFinite(distance) && distance <= SAME_PLACE_RADIUS_M && (!best || distance < best.distance)) {
      best = { label: place.label, distance };
    }
  }
  return best?.label ?? null;
}

/** The target model's place with the same name, if it has exactly one. */
export function matchingLaunchPlace<T extends LaunchStop>(label: string | null, stops: readonly T[]): T | null {
  const key = label ? launchPlaceNameKey(label) : "";
  if (!key) return null;
  const matches = stops.filter((stop) => launchPlaceNameKey(stop.label) === key);
  return matches.length === 1 ? matches[0]! : null;
}
